import { formatMoney } from "@upserve/financials";

import {
  groupBy,
  head,
  last,
  map,
  prop,
  propOr,
  reduceBy,
  sum,
  union,
} from "ramda";
import type {
  IItem,
  IItemRecord,
  IMenu,
  ISection,
} from "group_management/types";
import { type IKeyedGroups, flattenCollection } from "helpers/collections";

// Keys used to compare records, based on reconciliation criteria
// ==============================================================

// Display Group. Reconciled by NAME only: a display group's timing (always-show
// vs timed) is an editable per-location VARIATION of the same group, not a
// distinct entity, so the key must not encode timed-status. (Previously the key
// appended ":timed" whenever a timing_mask was present — but pos emits a mask
// even for always-show when the TIMED_DISPLAY_GROUPS feature is on, so that
// suffix split one group across feature-on/off locations and merged always-show
// with genuinely-timed same-name groups.)
const nonWordRegex = /\W/g;
export const getDGKey = ({ name }: ISection) =>
  name.replace(nonWordRegex, "-").toLocaleLowerCase();

// Menu
export const getMenuKey = (menu: IMenu) =>
  propOr<string, IMenu, string>("", "name", menu).toLocaleLowerCase();

// Item
export const getItemKey = ({ name, itemType }: IItem) =>
  `${name || ""}:${itemType}`.toLocaleLowerCase();

// Reconcilers
// ===========
// Take a reducer function that accepts array of T and returns U
// U defaults to IKeyedGroups<T> which is the output of groupBy()
function makeReconciler<T, U = IKeyedGroups<T>>(
  reduceFunc: (entries: T[]) => U
) {
  return (entries: T[] | IKeyedGroups<T> | T[][]) =>
    reduceFunc(flattenCollection(entries));
}
// Match menus by name
export const reconcileMenus = makeReconciler(groupBy(getMenuKey));
export const reconcileItems = makeReconciler(groupBy(getItemKey));

export interface IDGMeta {
  key: string;
  storeIds: string[];
  name: string;
  sections: ISection[];
  timed: boolean;
  totalItems: number;
}

export const getDGItemCount = prop<"itemsCount", number>("itemsCount");
export const sumDGItemCount = (sections: ISection[]) =>
  sum(map(getDGItemCount, sections));

function addDGToMeta(meta: IDGMeta, dg: ISection): IDGMeta {
  const { name, storeId, timingMask } = dg;
  const key = getDGKey(dg);
  return {
    key,
    storeIds: union(meta.storeIds, [storeId]),
    name,
    sections: meta.sections.concat(dg),
    // A reconciled group spans locations that may each be timed or always-show;
    // `timed` means "at least one location times this group".
    timed: meta.timed || !!timingMask,
    totalItems: meta.totalItems + getDGItemCount(dg),
  };
}

export const reconcileDGs = makeReconciler<
  ISection,
  { [index: string]: IDGMeta }
>(
  reduceBy<ISection, IDGMeta>(
    addDGToMeta,
    {
      key: "",
      storeIds: [],
      name: "",
      sections: [],
      timed: false,
      totalItems: 0,
    },
    getDGKey
  )
);

export const getItemPriceList = (items: IItemRecord[]) =>
  items.map(({ price }) => Number(price)).sort((a, b) => a - b);

export function itemPriceRange(items: IItemRecord[]): string {
  const prices = getItemPriceList(items);
  const low = head(prices);
  const high = last(prices);
  if (typeof low === "number" && typeof high === "number") {
    if (low === high) {
      return formatMoney(low);
    }
    return `${formatMoney(low)} - ${formatMoney(high)}`;
  }
  return "";
}
